import { create } from 'zustand';
import { authConfig, type AuthClient, type AuthConfig } from '../auth';
import type { LedgerSource, Me, OrganisationContext } from '../data/source';
import { defaultSource } from '../data/source';

/**
 * Where the app is with signing in:
 *  starting      checking for an existing sign-in
 *  signed_out    show the sign-in page
 *  no_books      signed in, but a member of no organisation yet
 *  choose_books  signed in to several organisations and none chosen
 *  ready         working in one organisation
 * With demo data (no VITE_API_URL) the app starts ready and never asks.
 */
export type Status = 'starting' | 'signed_out' | 'no_books' | 'choose_books' | 'ready';

const ORG_KEY = 'el.org';
const store = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private window */ } },
};

interface SessionState {
  source: LedgerSource;
  /** Null with demo data. */
  authConfig: AuthConfig | null;
  auth: AuthClient | null;
  status: Status;
  /** A message for the sign-in page, such as "Your sign-in has ended". */
  notice: string | null;
  me: Me | null;
  orgId: string | null;
  context: OrganisationContext | null;
  user: { name: string; initials: string; email: string | null };
  /** Overridable in tests and stories. */
  today: () => Date;
  /** Check the sign-in and choose the organisation; safe to call more than once. */
  start: () => Promise<void>;
  /** After a successful sign-in or callback. */
  refresh: () => Promise<void>;
  chooseOrg: (id: string) => Promise<void>;
  signOut: () => Promise<void>;
  load: () => Promise<void>;
  can: (capability: string) => boolean;
}

export function initials(name: string): string {
  const parts = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase();
}

let stopListening: (() => void) | null = null;

export const useSession = create<SessionState>((set, get) => {
  const cfg = authConfig();
  const ended = () => set({ status: 'signed_out', context: null, notice: 'Your sign-in has ended. Please sign in again.' });
  return {
    source: defaultSource(async () => (await get().auth?.accessToken()) ?? null, () => get().orgId, ended),
    authConfig: cfg,
    auth: null,
    status: cfg ? 'starting' : 'ready',
    notice: null,
    me: null,
    orgId: cfg ? null : store.get(ORG_KEY),
    context: null,
    user: { name: 'Brian', initials: 'BR', email: null },
    today: () => new Date(),

    start: async () => {
      const c = get().authConfig;
      if (!c) { await get().load(); return; }
      let auth = get().auth;
      if (!auth) {
        auth = await c.load();
        set({ auth });
        stopListening?.();
        stopListening = auth.onChange((u) => {
          if (!u && get().status !== 'signed_out') set({ status: 'signed_out', context: null, me: null });
        });
      }
      if (!(await auth.current())) { set({ status: 'signed_out' }); return; }
      await get().refresh();
    },

    refresh: async () => {
      const me = await get().source.me();
      const name = me.name ?? me.email ?? 'You';
      set({ me, notice: null, user: { name, initials: initials(name), email: me.email ?? null } });
      const stored = store.get(ORG_KEY);
      const pick = me.organisations.find((o) => o.id === get().orgId) ?? me.organisations.find((o) => o.id === stored)
        ?? (me.organisations.length === 1 ? me.organisations[0] : undefined);
      if (pick) await get().chooseOrg(pick.id);
      else set({ status: me.organisations.length ? 'choose_books' : 'no_books', orgId: null, context: null });
    },

    chooseOrg: async (id) => {
      store.set(ORG_KEY, id);
      set({ orgId: id, context: null, status: 'ready' });
      await get().load();
    },

    signOut: async () => {
      await get().auth?.signOut();
      set({ status: 'signed_out', me: null, context: null, orgId: null, notice: 'You’ve signed out.' });
    },

    load: async () => {
      const context = await get().source.organisation();
      set({ context });
    },

    can: (cap) => get().context?.capabilities.includes(cap) ?? false,
  };
});
